import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import {
  AppliedDiscountViewItem,
  DiscountAvailableStamp,
  DiscountLicenseSearchItem,
} from '../../../../../core/models/parking/discount-apply.model';
import { ParkingTransactionDetailResponse } from '../../../../../core/models/parking.model';
import { MapperDiscountApplyService } from '../../../../../core/mappers/parking/mapper-discount-apply.service';
import { MapperParkingMasterDataService } from '../../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiDiscountApplyService } from '../../../../../core/services/api/parking/api-discount-apply.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';

@Component({
  selector: 'app-stamp-apply',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent],
  templateUrl: './stamp-apply.component.html',
  styleUrl: './stamp-apply.component.css',
})
export class StampApplyComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiDiscountApplyService);
  private readonly mapper = inject(MapperDiscountApplyService);
  private readonly actionMapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);

  licenseInput = '';
  isSearching = signal(false);
  isLoadingDetail = signal(false);
  isApplying = signal(false);

  showSelectModal = signal(false);
  showStampModal = signal(false);
  showDiscountsModal = signal(false);

  searchResults = signal<DiscountLicenseSearchItem[]>([]);
  selectedSearchItem = signal<DiscountLicenseSearchItem | null>(null);
  detail = signal<ParkingTransactionDetailResponse | null>(null);
  availableStamps = signal<DiscountAvailableStamp[]>([]);
  pickerStamps = signal<DiscountAvailableStamp[]>([]);
  pickerTitle = signal('ตราประทับ');
  selectedStampId = signal(0);
  stampRemark = '';
  appliedDiscounts = signal<AppliedDiscountViewItem[]>([]);

  constructor() {
    effect(() => {
      const siteId = this.siteState.selectedSite()?.id;
      untracked(() => {
        if (!siteId) {
          this.clearAll(false);
        }
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get userId(): number {
    return Number(this.cookieService.get('user_id') || 0);
  }

  get pricing() {
    return this.detail()?.parkingPricing;
  }

  get durationText(): string {
    return this.mapper.formatDuration(this.detail()?.entryDate);
  }

  ngOnInit(): void {
    // page is search-first
  }

  onSearch(): void {
    const license = this.licenseInput.trim();
    if (!license) {
      this.toast.error('กรุณากรอกทะเบียนรถ');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจากแถบด้านบน');
      return;
    }

    this.isSearching.set(true);
    this.api.searchByLicense(license, this.selectedSiteId).subscribe({
      next: (res) => {
        console.log('searchByLicense', res);
        const list = this.mapper.mapSearchList(res);
        this.searchResults.set(list);
        this.isSearching.set(false);
        if (list.length === 0) {
          this.toast.error('ไม่พบรถที่ยังอยู่ในลาน');
          return;
        }
        this.showSelectModal.set(true);
      },
      error: (err) => {
        this.isSearching.set(false);
        this.toast.error(err?.error?.message || 'ค้นหาไม่สำเร็จ');
      },
    });
  }

  onClear(): void {
    this.clearAll(true);
  }

  closeSelectModal(): void {
    this.showSelectModal.set(false);
  }

  selectSearchItem(item: DiscountLicenseSearchItem): void {
    this.showSelectModal.set(false);
    this.selectedSearchItem.set(item);
    this.loadDetail(item.cardCode);
  }

  clearSelection(): void {
    this.selectedSearchItem.set(null);
    this.detail.set(null);
    this.availableStamps.set([]);
    this.appliedDiscounts.set([]);
  }

  photoUrl(item: DiscountLicenseSearchItem | null): string {
    if (!item) return 'img/notfound.jpg';
    return this.mapper.resolveCaptureUrl(item) || 'img/notfound.jpg';
  }

  openAllAvailableStamps(): void {
    if (!this.detail()) {
      this.toast.error('กรุณาเลือกรถก่อน');
      return;
    }
    const filtered = this.availableStamps().filter((s) => !s.alreadyStamped && s.hasQuotaRemaining);
    if (filtered.length === 0) {
      this.toast.error('ไม่มีตราที่ใช้งานได้');
      return;
    }
    this.pickerTitle.set('ตราประทับ');
    this.pickerStamps.set(filtered);
    this.selectedStampId.set(filtered[0]?.parkingRateId ?? 0);
    this.stampRemark = '';
    this.showStampModal.set(true);
  }

  openAllDiscounts(): void {
    if (!this.detail()) {
      this.toast.error('กรุณาเลือกรถก่อน');
      return;
    }
    this.appliedDiscounts.set(this.mapper.buildAppliedDiscounts(this.detail()));
    this.showDiscountsModal.set(true);
  }

  closeStampModal(): void {
    this.showStampModal.set(false);
  }

  closeDiscountsModal(): void {
    this.showDiscountsModal.set(false);
  }

  confirmApplyStamp(): void {
    const cardCode = this.detail()?.cardCode || this.selectedSearchItem()?.cardCode;
    const rateId = this.selectedStampId();
    if (!cardCode || !rateId) {
      this.toast.error('กรุณาเลือกตรา');
      return;
    }
    if (!this.userId) {
      this.toast.error('ไม่พบผู้ใช้');
      return;
    }

    this.isApplying.set(true);
    this.api.applyStamp({
      userId: this.userId,
      cardCode,
      parkingRateId: rateId,
      remark: this.stampRemark?.trim() || null,
    }).subscribe({
      next: (res) => {
        this.isApplying.set(false);
        const mapped = this.actionMapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'ให้ส่วนลดสำเร็จ');
          this.showStampModal.set(false);
          this.loadDetail(cardCode);
        } else {
          this.toast.error(mapped.message || 'ให้ส่วนลดไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isApplying.set(false);
        this.toast.error(err?.error?.message || 'ให้ส่วนลดไม่สำเร็จ');
      },
    });
  }

  selectStamp(id: number): void {
    this.selectedStampId.set(id);
  }

  private loadDetail(cardCode: string): void {
    if (!this.userId) {
      this.toast.error('ไม่พบผู้ใช้');
      return;
    }
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจากแถบด้านบน');
      return;
    }
    this.isLoadingDetail.set(true);
    this.api.getTransactionDetailByCardCode(cardCode, this.selectedSiteId, this.userId).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapDetail(res);
        this.detail.set(mapped.detail);
        this.availableStamps.set(mapped.availableStamps);
        this.isLoadingDetail.set(false);
      },
      error: (err) => {
        this.isLoadingDetail.set(false);
        this.detail.set(null);
        this.availableStamps.set([]);
        this.toast.error(err?.error?.message || 'โหลดรายละเอียดไม่สำเร็จ');
      },
    });
  }

  private clearAll(clearInput: boolean): void {
    if (clearInput) this.licenseInput = '';
    this.searchResults.set([]);
    this.showSelectModal.set(false);
    this.showStampModal.set(false);
    this.showDiscountsModal.set(false);
    this.clearSelection();
  }
}
